import {
  BrowserRouter as Router,
  Routes,
  Route,
  useSearchParams,
} from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import React, { Suspense, useEffect, useState } from "react";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import English from "./languages/English.json";
import { QueryClient, QueryClientProvider } from "react-query";
import { AllLanguagesCode, getAndSaveTimeZone, getLocalStorageMenuItem, setLocalStorageMenuItem, setMobileBackColor, setMobileSettings, loadAndChangeLanguage } from "./utility";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import axios from "axios";
import Loading from "./common/loading";
import TawkToChat from "./tawkToChat ";
// BottomNavBar and InstallPrompt are imported by individual screens, not here
import ScrollToTop from "./components/ScrollToTop";

// All screens lazy-loaded for optimal initial bundle size
const WellcomeScreen = React.lazy(() => import("./screens/wellcomeScreen"));
const Sections = React.lazy(() => import("./screens/sections"));
const NotFound = React.lazy(() => import("./screens/notFound"));
const Items = React.lazy(() => import("./screens/Item"));
const Cart = React.lazy(() => import("./screens/cart"));
const FeedBack = React.lazy(() => import("./screens/feedback"));
const Favorites = React.lazy(() => import("./screens/favorites"));
const Orders = React.lazy(() => import("./screens/orders"));
const OrderDetails = React.lazy(() => import("./screens/orderDetails"));
const OrderStatusTrack = React.lazy(() => import("./screens/orderStatusTrack"));
const OrderThanksScreen = React.lazy(() => import("./screens/orderThanksScreen"));
const MobilePaymentprocessing = React.lazy(() => import("./screens/mobilePaymentprocessing"));
const Profile = React.lazy(() => import("./screens/profile"));
const PhoneNumberVerificationScreen = React.lazy(() => import("./screens/phoneNumberVerificationScreen"));
const LabelsScreen = React.lazy(() => import("./screens/labelsScreen"));
const PrivacyPolicy = React.lazy(() => import("./screens/privacy_policy"));
const TermsAndConditions = React.lazy(() => import("./screens/terms_and_conditions"));
const Transactions = React.lazy(() => import("./screens/Transactions"));
const SavedCards = React.lazy(() => import("./screens/savedCards"));
const MyPoints = React.lazy(() => import("./screens/my_points"));
const Processing = React.lazy(() => import("./screens/processing"));

const detectedLanguage = AllLanguagesCode.filter((lang) => {
  return navigator.language.includes(lang);
});
const initialLang = getLocalStorageMenuItem("lang") || detectedLanguage[0] || "en";

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: English },
    },
    lng: "en", // Start with English; active language loaded async in App
    fallbackLng: "en",
    interpolation: {
      escapeValue: false,
    },
  });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const location = window.location.href;
  const array = location.split("/");
  const qr = array[array.length - 2];
  const qrName = array[array.length - 3];
  const [loadingTree, setLoadingTree] = useState(false);
  const [allTreeData, setAllTreeData] = useState(getLocalStorageMenuItem("tree_data") || [])
  const [langReady, setLangReady] = useState(initialLang === "en");

  const [lang, setLang] = useState();

  const storedLang = getLocalStorageMenuItem("lang");

  useEffect(() => {
    getAndSaveTimeZone();
    if (storedLang === "ar" || storedLang === "fa") {
      document.body.classList.add("arabic-font");
      document.body.classList.remove("latino-font");
      setLang("arabic-font");
    } else {
      document.body.classList.add("latino-font");
      document.body.classList.remove("arabic-font");
      setLang("latino-font");
    }
  }, [storedLang]);

  const loadTree = async () => {
    setLoadingTree(true);
    try {
      const timeZone = getLocalStorageMenuItem("timezone") || "";
      const promise = await axios.get(
        `${process.env.REACT_APP_API}/menus/${qr}/public-tree`,
        {
          headers: {
            localization: getLocalStorageMenuItem("lang") || "",
            timeZone: timeZone?.replace(/["]+/g, ''),
          },
        }
      );
      setLocalStorageMenuItem(
        "tree_data",
        JSON.stringify(promise?.data?.data ? promise?.data?.data : [])
      );
      setAllTreeData(promise?.data?.data ? promise?.data?.data : []);
      setMobileSettings(promise?.data?.data?.details)
      return promise?.data?.data;
    } catch (error) {
    } finally {
      setLoadingTree(false);
    }
  }

  // Load the active language bundle on mount (only English is pre-bundled)
  useEffect(() => {
    if (initialLang !== "en") {
      loadAndChangeLanguage(initialLang).then(() => setLangReady(true));
    }
  }, []);

  useEffect(() => {
    setLocalStorageMenuItem("prod_mode", "prod");
  }, []);



  const storedMode = getLocalStorageMenuItem("mode");

  useEffect(() => {
    if (storedMode === "dark") {
      document.body.classList.add("dark");
      document.body.classList.remove("light");
    } else {
      document.body.classList.add("light");
      document.body.classList.remove("dark");
    }
  }, [storedMode]);

  if (loadingTree || !langReady) {
    return <Loading backColor={setMobileBackColor()} />;
  }

  return (

    <div translate="no">
      <GoogleOAuthProvider clientId="987046823583-0i3q2mn69kg3qgr838jb0hes433vn67e.apps.googleusercontent.com">
        <QueryClientProvider client={queryClient}>
          <Router>
            <ScrollToTop />
            <TawkToChat />
            <Suspense fallback={<Loading backColor={setMobileBackColor()} />}>
            <Routes>
              <Route path="/trackorderstatus" element={<OrderStatusTrack />} />
              <Route
                path={`/:qrName/:qrCode/sections`}
                element={<Sections tableName={qrName} tableQr={qr} />}
              />

              <Route
                path={`/:qrName/:qrCode/labels`}
                element={<LabelsScreen tableName={qrName} tableQr={qr} />}
              />

              <Route
                path={`/:qrName/:qrCode/mobilePaymentprocessing`}
                element={<MobilePaymentprocessing tableName={qrName} tableQr={qr} />}
              />

              <Route
                path={`/:qrName/:qrCode/verify-phone`}
                element={<PhoneNumberVerificationScreen tableName={qrName} tableQr={qr} />}
              />
              <Route
                path={`/:qrName/:qrCode/items`}
                element={<Items tableName={qrName} tableQr={qr} />}
              />
              <Route
                path={`/:qrName/:qrCode/feedback`}
                element={<FeedBack tableName={qrName} tableQr={qr} />}
              />
              <Route
                path={`/:qrName/:qrCode/orders`}
                element={<Orders tableName={qrName} tableQr={qr} />}
              />
              <Route
                path={`/:qrName/:qrCode/saved-cards`}
                element={<SavedCards tableName={qrName} tableQr={qr} />}
              />
              <Route
                path={`/:qrName/:qrCode/order_thanks`}
                element={<OrderThanksScreen tableName={qrName} tableQr={qr} />}
              />
              <Route
                path={`/:qrName/:qrCode/order-details`}
                element={<OrderDetails tableName={qrName} tableQr={qr} />}
              />
              <Route
                path={`/:qrName/:qrCode/favorites`}
                element={<Favorites tableName={qrName} tableQr={qr} />}
              />
              <Route
                path={`/:qrName/:qrCode/cart`}
                element={<Cart tableName={qrName} tableQr={qr} />}
              />
              <Route
                path={`/${qrName}/${qr}/menus`}
                element={<WellcomeScreen tableName={qrName} tableQr={qr} />}
              />

              <Route
                path={`/${qrName}/${qr}/profile`}
                element={<Profile tableName={qrName} tableQr={qr} />}
              />

              <Route
                path={`/${qrName}/${qr}/my_points`}
                element={<MyPoints tableName={qrName} tableQr={qr} />}
              />
              <Route
                path={`/:qrName/:qrCode/transactions`}
                element={<Transactions tableName={qrName} tableQr={qr} />}
              />

              <Route
                path={`/${qrName}/${qr}/privacy-policy`}
                element={<PrivacyPolicy tableName={qrName} tableQr={qr} />}
              />

              <Route
                path={`/${qrName}/${qr}/terms-and-conditions`}
                element={<TermsAndConditions tableName={qrName} tableQr={qr} />}
              />

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </Router>
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </div>



  );
}

export default App;
